import autoTable from 'jspdf-autotable';

import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';
import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';

interface ConciliationDetail {
  documentId: string;
  amount: number;
  payInDate: string;
  typeEsp: string;
  type: string;
  paidBy: string;
}

interface ConciliationDetails {
  amountConciliated: number;
  amountConciliatedByFactoring: number;
  amountConciliatedByPayIns: number;
  amountConciliatedByFunds: number;
  amountConciliatedByPassives: number;
  amountConciliatedByDiscounts: number;
  amountConciliatedByCreditNotes: number;
  amountConciliatedByCreditRequests: number;
}

interface AccountReportDocument {
  id: string;
  folio: string;
  businessName: string;
  businessIdentifier: string;
  stakeholderName: string;
  stakeholderIdentifier: string;
  category: string;
  issuedDate: string;
  extraData: string;
  amount: number;
  amountConciliated: number;
  status: string;
  paidAt: string | null;
  conciliationDetails?: ConciliationDetails;
  conciliations?: ConciliationDetail[];
}

interface AccountReportConfig extends PDFGeneratorConfig {
  account: AccountReportDocument;
  accountType: 'Pagar' | 'Cobrar';
}

export class AccountReportGenerator extends BasePDFGenerator {
  private account: AccountReportDocument;
  private accountType: 'Pagar' | 'Cobrar';

  constructor(config: AccountReportConfig) {
    super(config);
    this.account = config.account;
    this.accountType = config.accountType;
  }

  protected addSummarySection(): void {
    const col1X = 10,
      col2X = 110,
      valueOffset = 35,
      initialY = 35;

    this.drawKeyValue(
      'Tipo de cuenta',
      `Por ${this.accountType.toLowerCase()}`,
      col1X,
      initialY,
      valueOffset,
    );
    this.drawKeyValue(
      'Id cuenta',
      this.account.folio || this.account.id,
      col1X,
      initialY + 5,
      valueOffset,
    );
    this.drawKeyValue(
      'Categoría',
      this.account.category || 'Sin categoría',
      col1X,
      initialY + 10,
      valueOffset,
    );
    this.drawKeyValue(
      'Fecha de emisión',
      this.account.issuedDate
        ? formatDate(new Date(this.account.issuedDate))
        : 'Sin fecha',
      col1X,
      initialY + 15,
      valueOffset,
    );

    if (this.accountType === 'Pagar') {
      this.drawKeyValue(
        'Acreedor',
        this.account.businessName || 'Sin nombre',
        col2X,
        initialY,
        valueOffset,
      );
      this.drawKeyValue(
        'RUT Acreedor',
        this.account.businessIdentifier || 'Sin RUT',
        col2X,
        initialY + 5,
        valueOffset,
      );
    } else {
      this.drawKeyValue(
        'Deudor',
        this.account.stakeholderName || 'Sin nombre',
        col2X,
        initialY,
        valueOffset,
      );
      this.drawKeyValue(
        'RUT Deudor',
        this.account.stakeholderIdentifier || 'Sin RUT',
        col2X,
        initialY + 5,
        valueOffset,
      );
    }

    this.drawKeyValue(
      'Monto total',
      formatMoney(this.account.amount || 0),
      col2X,
      initialY + 10,
      valueOffset,
    );
    this.drawKeyValue(
      'Monto conciliado',
      formatMoney(this.account.amountConciliated || 0),
      col2X,
      initialY + 15,
      valueOffset,
    );
  }

  protected addContentSection(): void {
    let y = 80;

    if (this.account.paidAt && this.account.status !== 'Pendiente') {
      this.drawText('Pago completado en fecha: ', 10, y, {
        size: 12,
        bold: true,
      });
      y += 5;
      this.drawText(formatDate(new Date(this.account.paidAt)), 10, y, {
        size: 10,
      });
      y += 15;
    }

    if (this.account.extraData) {
      this.drawText('Observaciones', 10, y, { size: 12, bold: true });
      y += 5;
      this.drawText(this.account.extraData, 10, y, { size: 10 });
      y += 15;
    }

    this.drawText('Resumen financiero', 10, y, { size: 12, bold: true });
    y += 5;

    const remainingAmount =
      (this.account.amount || 0) - (this.account.amountConciliated || 0);

    this.drawKeyValue(
      'Monto pendiente',
      formatMoney(remainingAmount),
      10,
      y,
      35,
    );

    if (this.account.amountConciliated > 0) {
      this.drawKeyValue(
        'Porcentaje pagado',
        `${((this.account.amountConciliated / this.account.amount) * 100).toFixed(1)}%`,
        110,
        y,
        35,
      );
    }

    if (this.account.conciliationDetails) {
      y += 20;
      this.drawText('Detalles de conciliación', 10, y, {
        size: 12,
        bold: true,
      });
      y += 10;

      const details = this.account.conciliationDetails;

      this.drawKeyValue(
        'Factoring',
        formatMoney(details.amountConciliatedByFactoring),
        10,
        y,
        35,
      );
      this.drawKeyValue(
        'Recaudación',
        formatMoney(details.amountConciliatedByPayIns),
        10,
        y + 5,
        35,
      );
      this.drawKeyValue(
        'Fondos',
        formatMoney(details.amountConciliatedByFunds),
        10,
        y + 10,
        35,
      );

      this.drawKeyValue(
        'Pasivos',
        formatMoney(details.amountConciliatedByPassives),
        110,
        y,
        35,
      );
      this.drawKeyValue(
        'Descuentos',
        formatMoney(details.amountConciliatedByDiscounts),
        110,
        y + 5,
        35,
      );
      this.drawKeyValue(
        'Notas crédito',
        formatMoney(details.amountConciliatedByCreditNotes),
        110,
        y + 10,
        35,
      );

      if (this.account.conciliations && this.account.conciliations.length > 0) {
        y += 25;
        this.drawText('Historial de conciliaciones', 10, y, {
          size: 12,
          bold: true,
        });
        y += 5;

        const tableData = this.account.conciliations.map(
          (conciliation: ConciliationDetail) => [
            formatDate(new Date(conciliation.payInDate)),
            conciliation.typeEsp,
            formatMoney(conciliation.amount),
            conciliation.paidBy,
          ],
        );

        autoTable(this.doc, {
          startY: y,
          head: [['Fecha', 'Tipo', 'Monto', 'Pagado por']],
          body: tableData,
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
          },
          styles: { fontSize: 8 },
          margin: { left: 10, right: 10 },
        });
      }
    } else {
      y += 20;
      this.drawText('Detalles de conciliación', 10, y, {
        size: 12,
        bold: true,
      });
      y += 10;
      this.drawText('No hay datos de conciliación disponibles', 10, y, {
        size: 10,
      });
    }
  }
}
